import React, { useState, useEffect } from 'react';
import '../GroupFieManager.css';

const CLOUD_URL = "https://photoselection-1-845189393450.asia-east1.run.app";

// Helper function to extract Drive ID from full share links
const extractDriveId = (input) => {
  if (!input) return '';
  
  // Matches /folders/ID or /file/d/ID
  const folderOrFileMatch = input.match(/\/(?:folders|file\/d)\/([a-zA-Z0-9_-]+)/);
  if (folderOrFileMatch && folderOrFileMatch[1]) {
    return folderOrFileMatch[1];
  }

  // Matches ?id=ID query parameter
  const idParamMatch = input.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idParamMatch && idParamMatch[1]) {
    return idParamMatch[1];
  }

  // Fallback: If it's already a clean ID string
  return input.trim();
};

export default function GroupFieManager() {
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [eventsList, setEventsList] = useState([]);
  
  // Search & Edit States
  const [searchFKey, setSearchFKey] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [editShortId, setEditShortId] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  // Form State
  const [eventForm, setEventForm] = useState({
    eventName: '',
    brandName: 'GroupFie',
    fKey: '',
    albumId: '',
    isGallery: true
  });

  // Fetch events on component load
  useEffect(() => {
    fetchAllEvents();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Automatically parse and extract ID if full Drive URL is pasted
    let processedValue = value;
    if (name === 'fKey' || name === 'albumId') {
      processedValue = extractDriveId(value);
    }

    setEventForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
  };

  // --- API CALLS ---

  const fetchAllEvents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CLOUD_URL}/api/get-all-events`);
      if (response.ok) {
        const data = await response.json();
        setEventsList(data);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async () => {
    if (!eventForm.eventName || !eventForm.fKey) {
      alert('Please fill in Event Name and Drive Link/ID.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${CLOUD_URL}/api/save-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventForm)
      });
      const data = await response.json();
      setGeneratedLink(`https://gallery.groupfie.in/gallery?id=${data.shortId}`);
      fetchAllEvents();
    } catch (err) {
      console.error('Error creating event:', err);
      alert('Failed to create event.');
    } finally {
      setLoading(false);
    }
  };

  const loadEventForEdit = async (shortIdToLoad) => {
    const idToFind = shortIdToLoad || editShortId.trim();
    if (!idToFind) {
      alert('Please enter a Short ID');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${CLOUD_URL}/api/get-event/${idToFind}`);
      if (!response.ok) throw new Error('Not found');
      
      const data = await response.json();
      setIsEditMode(true);
      setEditShortId(idToFind);
      setEventForm({
        eventName: data.eventName || '',
        brandName: data.brandName || 'GroupFie',
        fKey: data.fKey || '',
        albumId: data.albumId || '',
        isGallery: data.isGallery ?? true
      });
    } catch (err) {
      console.error('Fetch Error:', err);
      alert(`ID "${idToFind}" was not found in database.`);
    } finally {
      setLoading(false);
    }
  };

  const updateEvent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CLOUD_URL}/api/update-event/${editShortId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventForm)
      });
      if (response.ok) {
        alert('Event updated successfully!');
        setGeneratedLink(`https://gallery.groupfie.in/gallery?id=${editShortId}`);
        fetchAllEvents();
      } else {
        throw new Error('Update failed');
      }
    } catch (err) {
      alert('Failed to update event.');
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (shortId, eventName) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${eventName}" (${shortId})?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${CLOUD_URL}/api/delete-event/${shortId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        alert('Event deleted successfully!');
        fetchAllEvents();
        if (editShortId === shortId) {
          cancelEdit();
        }
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete event.');
    } finally {
      setLoading(false);
    }
  };

  const findEvent = async () => {
    if (!searchFKey) return;
    const cleanKey = extractDriveId(searchFKey);
    try {
      const response = await fetch(`${CLOUD_URL}/api/find-event-by-fkey?fKey=${cleanKey}`);
      if (!response.ok) throw new Error('Not found');
      
      const data = await response.json();
      setSearchResult({
        eventName: data.eventName,
        url: `https://gallery.groupfie.in/gallery?id=${data.shortId}`
      });
    } catch (err) {
      alert('Event not found. You can create a new one below.');
      setSearchResult(null);
    }
  };

  const cancelEdit = () => {
    setIsEditMode(false);
    setEditShortId('');
    setEventForm({
      eventName: '',
      brandName: 'GroupFie',
      fKey: '',
      albumId: '',
      isGallery: true
    });
  };

  const copyToClipboard = (text, message = 'Copied to clipboard!') => {
    navigator.clipboard.writeText(text);
    alert(message);
  };

  return (
    <div className="admin-container">
      <div className="admin-card">
        <div className="admin-header">
          <h1>GroupFie Link Generator</h1>
          <p>Create and manage short client access links</p>
        </div>

        <div className="dashboard-section animate-fade">
          {/* Quick Lookups Grid */}
          <div className="utilities-grid">
            <div className="utility-box">
              <h3>Quick Lookup (fKey / Link)</h3>
              <div className="search-flex">
                <input
                  type="text"
                  value={searchFKey}
                  onChange={(e) => setSearchFKey(e.target.value)}
                  placeholder="Paste Drive URL or fKey..."
                />
                <button onClick={findEvent} className="btn-icon">
                  <svg viewBox="0 0 24 24" width="18">
                    <path fill="currentColor" d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
                  </svg>
                </button>
              </div>
              {searchResult && (
                <div className="result-toast animate-fade">
                  <span>Found: {searchResult.eventName}</span>
                  <button onClick={() => copyToClipboard(searchResult.url, 'Link copied!')} className="btn-text">
                    Copy URL
                  </button>
                </div>
              )}
            </div>

            <div className="utility-box edit-lookup">
              <h3>Edit Existing Event</h3>
              <div className="search-flex">
                <input
                  type="text"
                  value={editShortId}
                  onChange={(e) => setEditShortId(e.target.value)}
                  placeholder="Enter Short ID..."
                />
                <button onClick={() => loadEventForEdit()} className="btn-icon btn-blue">
                  <svg viewBox="0 0 24 24" width="18">
                    <path fill="currentColor" d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="divider">
            <span>{isEditMode ? 'EDIT MODE' : 'CREATE NEW EVENT'}</span>
          </div>

          {/* Creation / Edit Form */}
          <div className="creation-form">
            <div className="input-group">
              <label>Event Name</label>
              <input
                name="eventName"
                value={eventForm.eventName}
                onChange={handleInputChange}
                placeholder="e.g. Royal Wedding"
              />
            </div>

            <div className="input-group">
              <label>Brand Name</label>
              <input
                name="brandName"
                value={eventForm.brandName}
                onChange={handleInputChange}
                placeholder="GroupFie"
              />
            </div>

            <div className="input-group">
              <label>Google Drive Folder Link or ID (fKey)</label>
              <input
                name="fKey"
                value={eventForm.fKey}
                onChange={handleInputChange}
                placeholder="Paste full Google Drive link or folder ID..."
              />
            </div>

            <div className="input-group">
              <label>Album PDF Link or ID (Optional)</label>
              <input
                name="albumId"
                value={eventForm.albumId}
                onChange={handleInputChange}
                placeholder="Paste full Google Drive PDF link or file ID..."
              />
            </div>

            <div className="toggle-group">
              <label className="switch-container">
                <input
                  type="checkbox"
                  name="isGallery"
                  checked={eventForm.isGallery}
                  onChange={handleInputChange}
                />
                <span className="slider"></span>
              </label>
              <span className="toggle-label">Enable Gallery View</span>
            </div>

            <div className="form-actions">
              {!isEditMode ? (
                <button onClick={createEvent} className="btn-auth" disabled={loading}>
                  {loading ? 'Generating...' : 'Generate Live Link'}
                </button>
              ) : (
                <div className="edit-actions-group">
                  <button onClick={updateEvent} className="btn-auth btn-blue" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button onClick={cancelEdit} className="btn-secondary">
                    Cancel / Reset Form
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Generated Link Alert */}
          {generatedLink && (
            <div className="success-result-box animate-fade">
              <div className="success-header">
                <svg viewBox="0 0 24 24" width="20" height="20" className="success-icon">
                  <path fill="currentColor" d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2m-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
                <span>Link Live!</span>
              </div>
              <div className="link-display">
                <input type="text" value={generatedLink} readOnly />
                <button onClick={() => copyToClipboard(generatedLink, 'Short URL copied!')} className="btn-copy">
                  Copy
                </button>
              </div>
              <button className="btn-reset" onClick={() => setGeneratedLink('')}>
                Dismiss Alert
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Events Table */}
     
    </div>
  );
}